import { useCallback, useState } from 'react'
import { useQuery } from 'react-query'

import type { BenchmarkingFeedService } from 'features/trend-detection/features/benchmarking-feed/service'
import type {
  AgentFeedOption,
  BenchmarkingFeedDirection,
  BenchmarkingFeedPeriod,
  BenchmarkingFeedSort,
  BenchmarkingInsightFeedItem,
  BenchmarkingInsightsPage,
} from 'features/trend-detection/types'

export const BENCHMARKING_FEED_PAGE_SIZE = 15
const DEFAULT_SORT: BenchmarkingFeedSort = 'weighted_z_score'

const EMPTY_PAGE: BenchmarkingInsightsPage = {
  items: [],
  page: 1,
  limit: BENCHMARKING_FEED_PAGE_SIZE,
  total_count: 0,
  total_pages: 0,
  outperforms_count: 0,
  underperforms_count: 0,
  has_historical_insights: false,
}

export interface UseBenchmarkingFeedParams {
  service: BenchmarkingFeedService
  projectId: string | null
  enabled: boolean
  /** Deep-link seed for the agent filter; subsequent changes go through
   * setAgentUuidFilter. */
  initialAgentUuidFilter?: string | null
  /** Deep-link seed for the period. */
  initialPeriod?: BenchmarkingFeedPeriod
}

export interface UseBenchmarkingFeedResult {
  items: BenchmarkingInsightFeedItem[]
  totalCount: number
  outperformsCount: number
  underperformsCount: number
  totalPages: number
  page: number
  isLoading: boolean
  hasHistoricalInsights: boolean
  agentsForFilter: AgentFeedOption[]
  agentUuidFilter: string | null
  setAgentUuidFilter: (uuid: string | null) => void
  directionFilter: BenchmarkingFeedDirection | null
  setDirectionFilter: (direction: BenchmarkingFeedDirection | null) => void
  period: BenchmarkingFeedPeriod
  setPeriod: (period: BenchmarkingFeedPeriod) => void
  sort: BenchmarkingFeedSort
  setSort: (sort: BenchmarkingFeedSort) => void
  onPageChange: (page: number) => void
}

export const useBenchmarkingFeed = ({
  service,
  projectId,
  enabled,
  initialAgentUuidFilter = null,
  initialPeriod = 'current',
}: UseBenchmarkingFeedParams): UseBenchmarkingFeedResult => {
  const [page, setPage] = useState(1)
  const [agentUuidFilter, setAgentUuidFilterState] = useState<string | null>(
    initialAgentUuidFilter,
  )
  const [directionFilter, setDirectionFilterState] =
    useState<BenchmarkingFeedDirection | null>(null)
  const [period, setPeriodState] =
    useState<BenchmarkingFeedPeriod>(initialPeriod)
  const [sort, setSortState] = useState<BenchmarkingFeedSort>(DEFAULT_SORT)

  // No keepPreviousData: changing any key part (page, sort, filter, period)
  // clears `data` to undefined so `isLoading` flips back to true and the
  // view shows its skeleton, matching the issue-feed UX.
  const { data = EMPTY_PAGE, isLoading } = useQuery<BenchmarkingInsightsPage>({
    queryKey: [
      'benchmarking-insights-feed',
      projectId,
      page,
      sort,
      agentUuidFilter,
      directionFilter,
      period,
    ],
    queryFn: () => {
      if (!projectId) return Promise.resolve(EMPTY_PAGE)
      return service.fetchBenchmarkingInsights({
        projectId,
        page,
        limit: BENCHMARKING_FEED_PAGE_SIZE,
        sort,
        agentUuid: agentUuidFilter,
        direction: directionFilter,
        period,
      })
    },
    enabled: enabled && !!projectId,
  })

  const { data: agentsForFilter = [] } = useQuery<AgentFeedOption[]>({
    queryKey: ['benchmarking-agent-options', projectId],
    queryFn: () => {
      if (!projectId) return Promise.resolve([])
      return service.fetchBenchmarkingAgentOptions(projectId)
    },
    enabled: enabled && !!projectId,
  })

  const setAgentUuidFilter = useCallback((uuid: string | null) => {
    setAgentUuidFilterState(uuid)
    setPage(1)
  }, [])

  const setDirectionFilter = useCallback(
    (direction: BenchmarkingFeedDirection | null) => {
      setDirectionFilterState(direction)
      setPage(1)
    },
    [],
  )

  const setPeriod = useCallback((next: BenchmarkingFeedPeriod) => {
    setPeriodState(next)
    setPage(1)
  }, [])

  const setSort = useCallback((next: BenchmarkingFeedSort) => {
    setSortState(next)
    setPage(1)
  }, [])

  const onPageChange = useCallback((next: number) => {
    setPage(next)
  }, [])

  return {
    items: data.items,
    totalCount: data.total_count,
    outperformsCount: data.outperforms_count,
    underperformsCount: data.underperforms_count,
    totalPages: data.total_pages,
    page,
    isLoading,
    hasHistoricalInsights: data.has_historical_insights,
    agentsForFilter,
    agentUuidFilter,
    setAgentUuidFilter,
    directionFilter,
    setDirectionFilter,
    period,
    setPeriod,
    sort,
    setSort,
    onPageChange,
  }
}
